import React, { useEffect, useState } from 'react';
import { fetchShellyDeviceReadings, ShellyDevice, ShellyReading } from '../../services/api';
import { colors, ui } from '../../styles/shared';
import { centerNote } from './helpers';

/** Värdet som visas i tabellen: text för valbara lägen, annars siffra eller sträng. */
const displayValue = (r: ShellyReading): React.ReactNode => {
    if (r.enumValues?.length && r.valueNum != null) {
        const match = r.enumValues.find(e => String(e.value) === String(r.valueNum));
        return match
            ? <>{match.text}<span style={{ color: '#aaa', fontWeight: 'normal', fontSize: 11, marginLeft: 6 }}>({r.valueNum})</span></>
            : String(r.valueNum);
    }
    if (r.valueNum != null) return r.valueNum.toLocaleString(undefined, { maximumFractionDigits: 4 });
    return r.valueStr ?? '—';
};

interface LiveDataTabProps {
    devices: ShellyDevice[];
    onError: (msg: string | null) => void;
    /** Går till fliken Enheter (länk i tomma listan). */
    onGoToDevices: () => void;
}

const LiveDataTab: React.FC<LiveDataTabProps> = ({ devices, onError, onGoToDevices }) => {
    const [selectedDeviceId, setSelectedDeviceId] = useState<string>(devices[0]?.device_id ?? '');
    const [readings, setReadings] = useState<ShellyReading[]>([]);
    const [readingsLoading, setReadingsLoading] = useState(false);
    const [filterText, setFilterText] = useState('');

    const loadReadings = async (deviceId: string) => {
        if (!deviceId) return;
        setReadingsLoading(true);
        try {
            setReadings(await fetchShellyDeviceReadings(deviceId));
        } catch (e: any) {
            onError(e.message || 'Kunde inte hämta värdena');
        } finally {
            setReadingsLoading(false);
        }
    };

    // Välj första enheten om listan kommer in efter att fliken öppnats
    useEffect(() => {
        if (!selectedDeviceId && devices.length > 0) setSelectedDeviceId(devices[0].device_id);
    }, [devices, selectedDeviceId]);

    useEffect(() => {
        if (selectedDeviceId) loadReadings(selectedDeviceId);
    }, [selectedDeviceId]); // eslint-disable-line react-hooks/exhaustive-deps

    const q = filterText.toLowerCase();
    const visible = readings.filter(r => !q || r.name.toLowerCase().includes(q) || r.key.toLowerCase().includes(q));

    if (devices.length === 0) {
        return (
            <div style={{ ...ui.card, textAlign: 'center', color: '#888' }}>
                Inga enheter tillagda än. Lägg till en under fliken <button onClick={onGoToDevices} style={{ background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', fontWeight: 'bold' }}>Enheter</button>.
            </div>
        );
    }

    return (
        <div>
            {/* Val av enhet, sökfält och uppdatera */}
            <div style={ui.card}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div>
                        <label style={{ ...ui.label, display: 'inline', marginRight: 8 }}>Enhet:</label>
                        <select
                            style={{ ...ui.input, width: 'auto', minWidth: 180 }}
                            value={selectedDeviceId}
                            onChange={e => setSelectedDeviceId(e.target.value)}
                        >
                            {devices.map(d => (
                                <option key={d.device_id} value={d.device_id}>{d.name}</option>
                            ))}
                        </select>
                    </div>
                    <input
                        style={{ ...ui.input, maxWidth: 200 }}
                        placeholder="Sök värde…"
                        value={filterText}
                        onChange={e => setFilterText(e.target.value)}
                    />
                    <button
                        style={{ ...ui.btn(colors.info), opacity: readingsLoading ? 0.6 : 1 }}
                        onClick={() => loadReadings(selectedDeviceId)}
                        disabled={readingsLoading}
                    >
                        {readingsLoading ? 'Hämtar…' : '↻ Uppdatera'}
                    </button>
                    <span style={{ ...ui.hint, marginTop: 0, marginLeft: 'auto' }}>
                        {visible.length} / {readings.length} värden
                    </span>
                </div>
            </div>

            {/* Tabell med värden */}
            {readingsLoading ? (
                <div style={centerNote('#888')}>Hämtar…</div>
            ) : readings.length === 0 ? (
                <div style={{ ...ui.card, textAlign: 'center', color: '#9ca3af' }}>
                    Inga värden ännu — vänta på nästa hämtning eller klicka på ↻ Uppdatera.
                </div>
            ) : (
                <div style={{ ...ui.card, padding: 0, overflow: 'hidden' }}>
                    <div className="tbl-scroll">
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ backgroundColor: colors.light }}>
                                    {['Parameter', 'Nyckel', 'Värde', 'Enhet', 'Uppdaterat'].map(h => (
                                        <th key={h} style={ui.th}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map((r, i) => (
                                    <tr key={r.key} style={{ backgroundColor: i % 2 === 0 ? colors.white : '#fafafa' }}>
                                        <td style={{ ...ui.td, fontWeight: 500 }}>{r.name}</td>
                                        <td style={{ ...ui.td, color: '#888', fontFamily: 'monospace', fontSize: 12 }}>{r.key}</td>
                                        <td style={{ ...ui.td, fontWeight: 'bold' }}>{displayValue(r)}</td>
                                        <td style={{ ...ui.td, color: colors.textMuted }}>{r.unit || '—'}</td>
                                        <td style={{ ...ui.td, color: '#aaa', fontSize: 12, whiteSpace: 'nowrap' }}>
                                            {r.timestamp ? new Date(r.timestamp).toLocaleString('sv-SE') : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LiveDataTab;
